'use client';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

const PHASES = [
  'Bootstrapping runtime environment...',
  'Compiling component tree...',
  'Hydrating agentic modules...',
  'Establishing secure connections...',
  'Applying ultra-premium UX layer...',
  'Systems nominal — entering at velocity.',
];

export default function PageLoader() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [topBarActive, setTopBarActive] = useState(false);
  const [topBarPct, setTopBarPct] = useState(0);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // FULL-PAGE LOADER — on first page load / refresh
  useEffect(() => {
    if (!isFirstLoad) return;

    let p = 0;
    const targets = [12, 28, 45, 62, 78, 91, 100];
    let phase = 0;

    const tick = () => {
      if (phase >= targets.length) return;
      p = targets[phase];
      setProgress(p);
      setPhaseIndex(phase);
      phase++;
      if (phase < targets.length) setTimeout(tick, phase === targets.length - 1 ? 600 : 380);
    };

    const startTimer = setTimeout(tick, 600);

    const dismiss = setTimeout(() => {
      setExiting(true);
      setTimeout(() => {
        setVisible(false);
        setIsFirstLoad(false);
      }, 700);
    }, 4200);

    return () => { clearTimeout(startTimer); clearTimeout(dismiss); };
  }, []);

  // TOP BAR LOADER — on every tab/route change after first load
  useEffect(() => {
    if (isFirstLoad) return;
    setTopBarActive(true);
    setTopBarPct(0);
    const t1 = setTimeout(() => setTopBarPct(85), 80);
    const t2 = setTimeout(() => setTopBarPct(100), 600);
    const t3 = setTimeout(() => { setTopBarActive(false); setTopBarPct(0); }, 900);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [pathname]);

  // Never show site chrome on print/PDF routes — it must render a clean document.
  if (pathname?.includes('/print') || pathname?.startsWith('/invoice')) return null;

  return (
    <>
      {/* TOP BAR for route changes */}
      {topBarActive && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: '3px',
          zIndex: 99999, background: 'transparent',
        }}>
          <div style={{
            height: '100%', background: '#7b6ff0',
            width: `${topBarPct}%`,
            transition: topBarPct === 85 ? 'width 0.5s cubic-bezier(0.4,0,0.2,1)' : 'width 0.2s ease',
            boxShadow: '0 0 12px 2px rgba(123, 111, 240,0.7)',
            position: 'relative',
          }}>
            <span style={{
              position: 'absolute', right: 0, top: '-3px',
              width: '3px', height: '9px', background: '#7b6ff0',
              boxShadow: '0 0 10px 3px rgba(123, 111, 240,0.9)',
            }} />
          </div>
        </div>
      )}

      {/* FULL-PAGE LOADER on first load */}
      {visible && (
        <div style={{
          position: 'fixed', inset: 0, background: '#000',
          zIndex: 99998, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: '"JetBrains Mono", monospace',
          transition: exiting ? 'opacity 0.7s ease, transform 0.7s ease' : 'none',
          opacity: exiting ? 0 : 1,
          transform: exiting ? 'scale(1.03)' : 'scale(1)',
        }}>
          {/* Scanlines */}
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(123, 111, 240,0.012) 2px,rgba(123, 111, 240,0.012) 4px)',
          }} />

          {/* Radial glow */}
          <div style={{
            position: 'absolute', width: 520, height: 520,
            background: 'radial-gradient(ellipse, rgba(123, 111, 240,0.07) 0%, transparent 70%)',
            top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            animation: 'dslGlow 3s ease-in-out infinite',
          }} />

          {/* Corner brackets */}
          {['tl','tr','bl','br'].map(c => (
            <div key={c} style={{
              position: 'absolute', width: 40, height: 40,
              ...(c==='tl'?{top:24,left:24}:c==='tr'?{top:24,right:24}:c==='bl'?{bottom:24,left:24}:{bottom:24,right:24}),
            }}>
              <div style={{
                position: 'absolute', background: '#7b6ff0', height: 1,
                width: '100%', ...(c.startsWith('b')?{bottom:0}:{top:0}),
                ...(c.endsWith('r')?{right:0}:{left:0}),
              }} />
              <div style={{
                position: 'absolute', background: '#7b6ff0', width: 1,
                height: '100%', ...(c.startsWith('b')?{bottom:0}:{top:0}),
                ...(c.endsWith('r')?{right:0}:{left:0}),
              }} />
            </div>
          ))}

          {/* Coord labels */}
          <span style={{position:'absolute',top:28,left:72,fontSize:9,color:'rgba(123, 111, 240,0.47)',letterSpacing:'0.15em'}}>X:00.00 / Y:00.00</span>
          <span style={{position:'absolute',top:28,right:72,fontSize:9,color:'rgba(123, 111, 240,0.47)',letterSpacing:'0.15em'}}>MUM / IND</span>
          <span style={{position:'absolute',bottom:28,left:72,fontSize:9,color:'rgba(123, 111, 240,0.47)',letterSpacing:'0.15em'}}>SYS:BOOT</span>

          {/* Ghost text */}
          <span style={{
            position:'absolute',fontSize:180,fontWeight:800,
            color:'rgba(255,255,255,0.015)',letterSpacing:'-0.08em',
            whiteSpace:'nowrap',userSelect:'none',
            top:'50%',transform:'translateY(-50%)',
          }}>DSL</span>

          {/* Center stage */}
          <div style={{position:'relative',zIndex:5,width:340,display:'flex',flexDirection:'column',alignItems:'center'}}>
            {/* Logo */}
            <div style={{display:'flex',alignItems:'baseline'}}>
              <img src="/logo/Primary_Light_Color.svg" alt="DOTSLASHLAB" className="h-32 w-auto dark:hidden" />
              <img src="/logo/Primary_Dark_Color.svg" alt="DOTSLASHLAB" className="h-32 w-auto hidden dark:block" />
            </div>
            <div style={{fontSize:10,fontWeight:300,color:'rgba(255,255,255,0.47)',letterSpacing:'0.35em',textTransform:'uppercase',marginTop:6}}>
              Operating at terminal velocity
            </div>

            {/* Divider */}
            <div style={{width:'100%',height:1,background:'rgba(255,255,255,0.06)',margin:'28px 0 24px'}} />

            {/* Progress */}
            <div style={{width:'100%'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                <span style={{fontSize:9,color:'rgba(123, 111, 240,0.5)',letterSpacing:'0.2em',textTransform:'uppercase'}}>
                  {phaseIndex < 6 ? ['Initializing','Loading assets','Configuring','Syncing','Optimizing','Almost ready'][phaseIndex] : 'Launch ready'}
                </span>
                <span style={{fontSize:11,fontWeight:700,color:'#7b6ff0'}}>{progress}%</span>
              </div>
              {/* 3 stacked bars */}
              {[1,0.35,0.15].map((opacity,i) => (
                <div key={i} style={{
                  width:'100%', height: i===0?3:2,
                  background:'rgba(255,255,255,0.05)',
                  marginBottom: i<2?5:0, overflow:'hidden', position:'relative',
                }}>
                  <div style={{
                    height:'100%',
                    background:`rgba(123, 111, 240,${opacity})`,
                    width:`100%`,
                    transform: `scaleX(${Math.min(progress + i*5, 100) / 100})`,
                    transformOrigin: 'left',
                    transition:'transform 0.12s linear',
                    position:'relative',
                  }}>
                    {i===0 && (
                      <span style={{
                        position:'absolute',right:0,top:-4,
                        width:2,height:10,background:'#7b6ff0',
                        boxShadow:'0 0 14px 4px rgba(123, 111, 240,0.85)',
                      }} />
                    )}
                  </div>
                </div>
              ))}
              {/* Status */}
              <div style={{marginTop:16,height:18,overflow:'hidden'}}>
                <p style={{fontSize:10,color:'rgba(255,255,255,0.47)',letterSpacing:'0.06em',lineHeight:'18px',margin:0}}>
                  › {PHASES[Math.min(phaseIndex, PHASES.length-1)]}
                </p>
              </div>
            </div>

            {/* Metrics */}
            <div style={{
              display:'flex',width:'100%',marginTop:28,
              border:'1px solid rgba(255,255,255,0.05)',
            }}>
              {[['40+','Products'],['$8M+','Raised'],['21d','Avg MVP']].map(([val,lbl],i) => (
                <div key={i} style={{
                  flex:1,padding:'10px 0',textAlign:'center',
                  borderRight: i<2 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                }}>
                  <div style={{fontSize:14,fontWeight:700,color:'#fff'}}>
                    <span style={{color:'#7b6ff0'}}>{val}</span>
                  </div>
                  <div style={{fontSize:8,color:'rgba(255,255,255,0.47)',letterSpacing:'0.15em',textTransform:'uppercase',marginTop:3}}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>

          <style>{`
            @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;700;800&display=swap');
            @keyframes dslGlow { 0%,100%{opacity:0.6;transform:translate(-50%,-50%) scale(1)} 50%{opacity:1;transform:translate(-50%,-50%) scale(1.15)} }
          `}</style>
        </div>
      )}
    </>
  );
}
